// Marketing attribution: capture click IDs and UTM params on landing,
// persist for 90 days, and surface for checkout / dataLayer.

const STORAGE_KEY = "pk_attribution_v1";
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

const CLICK_ID_KEYS = ["gclid", "fbclid", "ttclid", "msclkid", "wbraid", "gbraid"] as const;
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

export interface Attribution {
  // Click IDs from ad platforms
  gclid?: string;
  fbclid?: string;
  ttclid?: string;
  msclkid?: string;
  wbraid?: string;
  gbraid?: string;
  // UTM params
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  // Context
  landing_page?: string;
  referrer?: string;
  first_seen_at?: string;
}

function safeRead(): Attribution | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Attribution;
    if (parsed.first_seen_at) {
      const age = Date.now() - new Date(parsed.first_seen_at).getTime();
      if (age > MAX_AGE_MS) return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function safeWrite(a: Attribution) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(a));
  } catch {
    // ignore
  }
}

/**
 * Run once on app boot. If the current URL contains any tracked params,
 * stash them in localStorage. First-touch wins (we don't overwrite an
 * existing record unless new click IDs arrive).
 */
export function captureAttributionFromUrl() {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);

  const incoming: Attribution = {};
  let hasAny = false;

  for (const k of CLICK_ID_KEYS) {
    const v = params.get(k);
    if (v) {
      (incoming as Record<string, string>)[k] = v;
      hasAny = true;
    }
  }
  for (const k of UTM_KEYS) {
    const v = params.get(k);
    if (v) {
      (incoming as Record<string, string>)[k] = v;
      hasAny = true;
    }
  }

  const existing = safeRead();

  // First-touch attribution: keep the original record unless this visit
  // brought a fresh click ID (re-click on a new ad).
  if (existing && !hasAny) return;

  const merged: Attribution = {
    ...(existing ?? {}),
    ...incoming,
    landing_page: existing?.landing_page ?? window.location.href,
    referrer: existing?.referrer ?? (document.referrer || undefined),
    first_seen_at: existing?.first_seen_at ?? new Date().toISOString(),
  };

  safeWrite(merged);
}

export function getAttribution(): Attribution | null {
  return safeRead();
}

export function clearAttribution() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
